/**
 * Printer tab state for the `?printer=` URL param.
 *
 * Returns every enabled shared printer configured on this PC plus a
 * "Browser dialog" target, and the currently selected target. The selection
 * lives in the URL so it survives reloads and can be shared.
 */
import { useSearchParams } from 'react-router-dom';
import {
  ConnectedDevice,
  PRINTER_DEVICE_TYPES,
  getDevices,
  getDocumentPrinter,
} from '../utils/devices';

export const PRINT_TARGET_PARAM = 'printer';
export const PRINT_DIALOG_TARGET = 'dialog';

export interface PrinterTabState {
  printers: ConnectedDevice[];
  /** Active target id: a device id, or PRINT_DIALOG_TARGET. */
  activeId: string;
  /** Active device, or null when the browser dialog target is selected. */
  activeDevice: ConnectedDevice | null;
  setActiveId: (id: string) => void;
}

export function usePrinterTab(defaultId?: string): PrinterTabState {
  const [params, setParams] = useSearchParams();

  const printers = getDevices().filter(
    (d) => d.enabled && d.shareName && PRINTER_DEVICE_TYPES.includes(d.type),
  );

  const fallbackId = defaultId || getDocumentPrinter()?.id || PRINT_DIALOG_TARGET;
  const requested = params.get(PRINT_TARGET_PARAM) || fallbackId;
  const activeDevice = printers.find((p) => p.id === requested) ?? null;
  const activeId = activeDevice ? activeDevice.id : PRINT_DIALOG_TARGET;

  const setActiveId = (id: string) => {
    const next = new URLSearchParams(params);
    next.set(PRINT_TARGET_PARAM, id);
    setParams(next, { replace: true });
  };

  return { printers, activeId, activeDevice, setActiveId };
}
